/**
 * As três formas de gráfico do §3 do design system, em SVG à mão.
 *
 * **Sem biblioteca, e a razão não é peso.** Uma dependência de gráfico entra no runtime, e
 * runtime passa pelo gate de licenças do `F-05` (ADR-003) — para desenhar barras, uma linha
 * e uma rosca, que é geometria de colégio. O que a biblioteca traria de verdade é a
 * tentação de usar o que ela oferece: gradiente, sombra, 3D, animação de entrada, tooltip
 * com estilo próprio. O §3 proíbe os quatro primeiros e o design system define o quinto.
 *
 * **Todo elemento é clicável, porque todo elemento é um recorte** (§4.1). Uma barra que não
 * leva ao que ela conta é a mesma coisa que um KPI que não filtra: um número que ninguém
 * confere. Por isso cada forma recebe `aoAbrir` e o rótulo diz quantos vai abrir.
 *
 * Acessibilidade não é enfeite aqui: gráfico é a parte da tela que mais depende de visão, e
 * o §6 diz que estado comunicado só por cor não chega em quem não distingue as cores. Cada
 * forma tem `role="img"` com descrição, e os elementos clicáveis são `<button>` de verdade
 * ou trazem `<title>` — quem navega por teclado ou leitor de tela recebe os números.
 */
import { useEffect, useState } from "react";
import type { ReactNode } from "react";

import { numeroLongo, reais, reaisCompacto } from "../formato";

/** Um ponto de série: o que a barra mede e o que a linha mede, no mesmo dia. */
export interface PontoDeVolume {
  dia: string;
  transitos: number;
  fora_do_sla: number;
}

/** Uma fatia de rosca ou linha de ranking. `quantidade` é o que o clique abre. */
export interface FatiaDeGrafico {
  chave: string;
  rotulo: string;
  quantidade: number;
  /** O que aquela fatia vale em centavos. `null` quando não há tarifa para somá-la. */
  valor_centavos?: number | null;
  nota?: string | null;
}

function diaCurto(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  });
}

/**
 * Barras de volume com a linha do que saiu do prazo sobreposta (`O-12`).
 *
 * **Duas escalas, e o eixo da direita é sempre o da linha** (§3). As grandezas diferem em
 * ordens de magnitude — num dia normal passam centenas de trânsitos e poucos estouram as
 * 24h —, e uma escala só faria a linha colar no zero justamente no dia em que ela importa.
 *
 * A linha usa `--vermelho` **não** por estética: ela é dinheiro que ainda dá para salvar.
 */
export function BarrasComLinha({
  pontos,
  aoAbrirDia,
}: {
  pontos: PontoDeVolume[];
  aoAbrirDia?: (dia: string) => void;
}) {
  // A largura é a do painel, medida (21/09): com 46 px fixos por dia, trinta dias davam
  // 1.450 px de SVG encolhidos para caber em 430 — barras de 7 px e rótulos ilegíveis, e a
  // Gisele pediu "a tela de indicadores bonita assim", com o mockup de barras cheias.
  const [caixa, definirCaixa] = useState<HTMLDivElement | null>(null);
  const [largura, definirLargura] = useState(640);
  useEffect(() => {
    if (!caixa) return;
    const medir = () => definirLargura(Math.max(280, caixa.clientWidth));
    medir();
    if (typeof ResizeObserver === "undefined") return;
    const observador = new ResizeObserver(medir);
    observador.observe(caixa);
    return () => observador.disconnect();
  }, [caixa]);

  if (pontos.length === 0) {
    return <Vazio>Nenhum trânsito no período escolhido.</Vazio>;
  }

  const L = 44; // eixo esquerdo: volume
  const R = 40; // eixo direito: o da linha
  const T = 10; // folga do topo
  const A = 210; // altura da área de plotagem
  const B = 20; // faixa dos rótulos do eixo x
  const faixa = (largura - L - R) / pontos.length;
  const barra = Math.max(3, Math.min(34, faixa * 0.62));

  // Os máximos nunca são zero: dividir por zero desenharia `NaN` no `d` do path, e o
  // sintoma seria o gráfico simplesmente não aparecer, sem erro nenhum no console.
  const maxBarra = Math.max(1, ...pontos.map((p) => p.transitos));
  const maxLinha = Math.max(1, ...pontos.map((p) => p.fora_do_sla));

  const x = (i: number) => L + i * faixa + faixa / 2;
  const yBarra = (v: number) => T + A - (v / maxBarra) * A;
  const yLinha = (v: number) => T + A - (v / maxLinha) * A;
  // Um rótulo de dia a cada ~52 px: com trinta dias num painel estreito, rótulo em todo dia
  // vira uma faixa cinza.
  const passo = Math.max(1, Math.ceil(52 / faixa));

  const caminho = pontos
    .map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${yLinha(p.fora_do_sla)}`)
    .join(" ");

  return (
    <figure className="grid gap-2">
      <div ref={definirCaixa} className="min-w-0">
        <svg
          viewBox={`0 0 ${largura} ${T + A + B}`}
          width={largura}
          height={T + A + B}
          className="block max-w-full"
          role="img"
          aria-label={`Volume por dia, de ${diaCurto(pontos[0]!.dia)} a ${diaCurto(
            pontos[pontos.length - 1]!.dia,
          )}. Barras: trânsitos. Linha: não enviados em 24h.`}
        >
          {/* Quatro linhas de grade, com o valor de cada uma nos dois eixos. */}
          {[0, 0.25, 0.5, 0.75, 1].map((f) => {
            const y = T + A - f * A;
            return (
              <g key={f}>
                <line
                  x1={L}
                  x2={largura - R}
                  y1={y}
                  y2={y}
                  className="stroke-linha-suave"
                  strokeWidth={1}
                  strokeDasharray={f === 0 ? undefined : "3 4"}
                />
                <text
                  x={L - 6}
                  y={y + 3}
                  textAnchor="end"
                  className="fill-tinta-3 text-[9.5px]"
                >
                  {compacto(maxBarra * f)}
                </text>
                <text
                  x={largura - R + 6}
                  y={y + 3}
                  className="fill-vermelho text-[9.5px]"
                >
                  {compacto(maxLinha * f)}
                </text>
              </g>
            );
          })}

          {pontos.map((p, i) => (
            <g key={p.dia}>
              <title>
                {diaCurto(p.dia)}: {numeroLongo(p.transitos)} trânsito(s),{" "}
                {numeroLongo(p.fora_do_sla)} fora do SLA
              </title>
              <rect
                x={x(i) - barra / 2}
                y={yBarra(p.transitos)}
                width={barra}
                height={Math.max(1, T + A - yBarra(p.transitos))}
                rx={Math.min(3, barra / 3)}
                className={`fill-azul/75 ${aoAbrirDia ? "cursor-pointer hover:fill-azul" : ""}`}
                onClick={aoAbrirDia ? () => aoAbrirDia(p.dia) : undefined}
              />
              {i % passo === 0 && (
                <text
                  x={x(i)}
                  y={T + A + 14}
                  textAnchor="middle"
                  className="fill-tinta-3 text-[9.5px]"
                >
                  {diaCurto(p.dia)}
                </text>
              )}
            </g>
          ))}

          <path
            d={caminho}
            fill="none"
            className="stroke-vermelho"
            strokeWidth={2}
            strokeLinejoin="round"
          />
          {pontos.map((p, i) => (
            <circle
              key={p.dia}
              cx={x(i)}
              cy={yLinha(p.fora_do_sla)}
              r={faixa > 14 ? 3 : 2}
              className="fill-vermelho"
            />
          ))}
        </svg>
      </div>
      <figcaption className="flex flex-wrap items-center gap-3 text-[10.5px] text-tinta-3">
        <Legenda cor="bg-azul/75">trânsitos no dia (eixo esquerdo)</Legenda>
        <Legenda cor="bg-vermelho">não enviados em 24h (eixo direito)</Legenda>
      </figcaption>
    </figure>
  );
}

/** `12,5 mil`, `1,4 mi` — o eixo tem cinco números, e cinco números longos não cabem. */
function compacto(v: number): string {
  return Math.round(v).toLocaleString("pt-BR", {
    notation: "compact",
    maximumFractionDigits: 1,
  });
}

/**
 * Rosca de composição, com a legenda **fora** do gráfico (§3).
 *
 * Valor absoluto e percentual lado a lado, porque percentual sozinho esconde a ordem de
 * grandeza e valor sozinho esconde a proporção. No máximo cinco fatias — acima disso a
 * rosca vira um mosaico que ninguém lê de longe.
 */
export function Rosca({
  fatias,
  aoAbrir,
  rotuloDoTotal,
}: {
  fatias: FatiaDeGrafico[];
  aoAbrir?: (chave: string) => void;
  rotuloDoTotal: string;
}) {
  // Qual fatia está em foco — pelo mouse na rosca **ou** na legenda. Em foco, ela ganha
  // espessura, as outras apagam, e o miolo passa a dizer o número dela (22/09: "melhorar a
  // pizza" — a rosca só dizia o total, e para ler uma fatia era preciso caçar o title).
  const [foco, definirFoco] = useState<string | null>(null);
  const total = fatias.reduce((s, f) => s + f.quantidade, 0);
  const arrecadado = fatias.every((f) => f.valor_centavos != null)
    ? fatias.reduce((s, f) => s + (f.valor_centavos ?? 0), 0)
    : null;
  if (total === 0) return <Vazio>Nada arrecadado no período escolhido.</Vazio>;

  const RAIO = 54;
  const ESPESSURA = 16;
  const circunferencia = 2 * Math.PI * RAIO;
  // Um respiro entre as fatias: sem ele, verde-escuro encostado em azul-escuro vira uma
  // mancha só no projetor. Fatia menor que o respiro some — por isso o mínimo de 0,5.
  const RESPIRO = fatias.length > 1 ? 1.6 : 0;
  let percorrido = 0;
  const emFoco = fatias.find((f) => f.chave === foco) ?? null;

  return (
    <figure className="grid justify-items-center gap-3">
      <div className="relative shrink-0" onMouseLeave={() => definirFoco(null)}>
        <svg
          viewBox="0 0 140 140"
          className="h-[190px] w-[190px] -rotate-90"
          role="img"
          aria-label={`Composição: ${fatias
            .map((f) => `${f.rotulo}, ${numeroLongo(f.quantidade)}`)
            .join("; ")}.`}
        >
          <circle
            cx={70}
            cy={70}
            r={RAIO}
            fill="none"
            strokeWidth={ESPESSURA}
            className="stroke-painel-2"
          />
          {fatias.map((f, i) => {
            const fracao = f.quantidade / total;
            const traco = Math.max(0.5, fracao * circunferencia - RESPIRO);
            const deslocamento = -percorrido * circunferencia;
            percorrido += fracao;
            const apagada = foco !== null && foco !== f.chave;
            return (
              <circle
                key={f.chave}
                cx={70}
                cy={70}
                r={RAIO}
                fill="none"
                strokeWidth={foco === f.chave ? ESPESSURA + 5 : ESPESSURA}
                strokeDasharray={`${traco} ${circunferencia - traco}`}
                strokeDashoffset={deslocamento}
                className={`${TOM_DA_FATIA[i % TOM_DA_FATIA.length]} transition-[opacity,stroke-width] duration-150 ${
                  apagada ? "opacity-30" : ""
                } ${aoAbrir ? "cursor-pointer" : ""}`}
                onMouseEnter={() => definirFoco(f.chave)}
                onClick={aoAbrir ? () => aoAbrir(f.chave) : undefined}
              >
                <title>
                  {f.rotulo}: {numeroLongo(f.quantidade)} (
                  {porcento(f.quantidade, total)})
                  {f.valor_centavos == null
                    ? ""
                    : ` — ${reais(f.valor_centavos)}`}
                </title>
              </circle>
            );
          })}
        </svg>
        {/* O miolo: o total, ou a fatia em foco. */}
        <span className="pointer-events-none absolute inset-0 grid place-content-center px-8 text-center">
          {emFoco ? (
            <>
              <span className="mono text-[20px] leading-none font-extrabold text-tinta">
                {porcento(emFoco.quantidade, total)}
              </span>
              <span className="mt-1 text-[10.5px] leading-tight text-tinta-2">
                {emFoco.rotulo}
              </span>
              <span className="mono text-[10px] text-tinta-3">
                {emFoco.valor_centavos == null
                  ? `${compacto(emFoco.quantidade)} trânsitos`
                  : reaisCompacto(emFoco.valor_centavos)}
              </span>
            </>
          ) : (
            <>
              <span className="mono text-[17px] leading-none font-extrabold text-tinta">
                {arrecadado == null
                  ? compacto(total)
                  : reaisCompacto(arrecadado)}
              </span>
              <span className="mt-1 text-[10px] text-tinta-3">
                {arrecadado == null ? "trânsitos" : "arrecadado"}
              </span>
            </>
          )}
        </span>
      </div>

      <figcaption className="grid w-full min-w-0 gap-1">
        <span className="text-center text-[11px] text-tinta-3">
          {numeroLongo(total)} {rotuloDoTotal}
          {/* O total em reais só quando **toda** fatia tem valor — somar as que têm e
              omitir as que não têm daria um total menor com cara de total. */}
          {arrecadado == null ? "" : ` · ${reais(arrecadado)}`}
        </span>
        {/* A legenda em largura inteira, embaixo da rosca: ao lado, num painel de um terço,
            "Aguardando pagamento" virava "Aguardando pa…" (22/09). Rótulo inteiro, o número,
            o percentual e — quando há tarifa — o valor da fatia. */}
        <ul className="grid gap-0.5" onMouseLeave={() => definirFoco(null)}>
          {fatias.map((f, i) => (
            <li key={f.chave}>
              <button
                type="button"
                disabled={!aoAbrir}
                onClick={() => aoAbrir?.(f.chave)}
                onMouseEnter={() => definirFoco(f.chave)}
                onFocus={() => definirFoco(f.chave)}
                onBlur={() => definirFoco(null)}
                className={`grid w-full grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-x-2 rounded-botao px-1.5 py-1 text-left text-[11.5px] enabled:hover:bg-realce ${
                  foco === f.chave ? "bg-realce" : ""
                }`}
              >
                <span
                  className={`size-2.5 shrink-0 rounded-[2px] ${FUNDO_DA_FATIA[i % FUNDO_DA_FATIA.length]}`}
                />
                <span className="min-w-0 leading-tight text-tinta-2">
                  {f.rotulo}
                  {f.valor_centavos != null && (
                    <small className="mono block text-[10px] text-tinta-3">
                      {reaisCompacto(f.valor_centavos)}
                    </small>
                  )}
                </span>
                <span className="mono text-right text-tinta">
                  {numeroLongo(f.quantidade)}
                </span>
                <span className="mono w-11 text-right font-semibold text-tinta-2">
                  {porcento(f.quantidade, total)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </figcaption>
    </figure>
  );
}

/** Percentual com uma casa quando a fatia é pequena: "0%" numa fatia que existe mente. */
function porcento(parte: number, total: number): string {
  const p = (100 * parte) / total;
  if (p > 0 && p < 1)
    return `${p.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
  return `${Math.round(p)}%`;
}

/**
 * Ranking com barra proporcional inline (§3).
 *
 * **A barra é sempre relativa ao primeiro colocado, nunca ao total.** Relativa ao total,
 * cinco causas equilibradas viram cinco tracinhos idênticos e a tabela perde a única coisa
 * que ela faz melhor do que uma lista: ser lida de longe.
 */
export function Ranking({
  linhas,
  aoAbrir,
  unidade,
}: {
  linhas: FatiaDeGrafico[];
  aoAbrir?: (chave: string) => void;
  unidade: string;
}) {
  if (linhas.length === 0) {
    return <Vazio>Nenhuma perda registrada no período escolhido.</Vazio>;
  }
  const maior = Math.max(...linhas.map((l) => l.quantidade));

  return (
    <ol aria-label="Motivos de perda" className="grid gap-1.5">
      {linhas.map((linha, i) => (
        <li key={linha.chave}>
          <button
            type="button"
            disabled={!aoAbrir}
            onClick={() => aoAbrir?.(linha.chave)}
            className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-2 rounded-[7px] border border-linha bg-painel-2 px-2.5 py-2 text-left enabled:hover:bg-realce"
          >
            <span className="mono grid size-6 place-items-center rounded-[5px] border border-linha text-[10.5px] text-tinta-3">
              {i + 1}
            </span>
            <span className="grid min-w-0 gap-1">
              <span className="truncate text-[12px] text-tinta">
                {linha.rotulo}
              </span>
              <span className="h-1.5 rounded-full bg-painel">
                <span
                  className="block h-1.5 rounded-full bg-vermelho"
                  style={{
                    width: `${Math.max(4, (linha.quantidade / maior) * 100)}%`,
                  }}
                />
              </span>
              {linha.nota && (
                <small className="text-[10px] wrap-anywhere text-tinta-3">
                  {linha.nota}
                </small>
              )}
            </span>
            {/* **Dinheiro primeiro, quando há dinheiro.** Este ranking é a tela mais
                acionável do produto, e a diferença entre "27.126 trânsitos" e
                "R$ 830 mil" é a diferença entre um relatório e uma decisão. A contagem
                fica logo abaixo, porque é ela que o clique abre. */}
            <span className="grid justify-items-end">
              {linha.valor_centavos == null ? (
                <>
                  <span className="mono text-[15px] font-bold text-tinta">
                    {numeroLongo(linha.quantidade)}
                  </span>
                  <small className="text-[9.5px] text-tinta-3">{unidade}</small>
                </>
              ) : (
                <>
                  <span
                    className="mono text-[15px] font-bold text-tinta"
                    title={reais(linha.valor_centavos)}
                  >
                    {reaisCompacto(linha.valor_centavos)}
                  </span>
                  <small className="text-[9.5px] text-tinta-3">
                    {numeroLongo(linha.quantidade)} {unidade}
                  </small>
                </>
              )}
            </span>
          </button>
        </li>
      ))}
    </ol>
  );
}

/**
 * As cores das fatias, na ordem.
 *
 * Literais e não geradas: o Tailwind varre o código em busca de nomes de classe completos,
 * e uma classe montada por interpolação simplesmente não existe no CSS final — o sintoma é
 * a fatia sair transparente, sem erro nenhum.
 */
const TOM_DA_FATIA = [
  "stroke-verde",
  "stroke-azul-cheio",
  "stroke-amarelo",
  "stroke-azul-claro",
  "stroke-cinza",
];
const FUNDO_DA_FATIA = [
  "bg-verde",
  "bg-azul-cheio",
  "bg-amarelo",
  "bg-azul-claro",
  "bg-cinza",
];

/** "12/10" de "2026-10-12": o dia já é de Brasília, e `new Date` o leria em UTC. */
function diaDoCalendario(dia: string): string {
  const [, mes, d] = dia.split("-");
  return `${d}/${mes}`;
}

/** Um dia da tendência de um índice: o mês acumulado até ele, já truncado pelo servidor. */
export interface PontoDaTendencia {
  dia: string;
  percentual: string | null;
}

/**
 * A tendência do mês de um índice (`PC-08`): o acumulado do dia 1 até cada dia, com a meta
 * tracejada. Uma escala só, em pontos percentuais, do menor valor (ou da meta) até 100.
 *
 * **A linha é da série, não da situação**: azul, sempre. A situação do índice já está no
 * selo e no número do cartão, com o texto (§6); pintar a linha de vermelho seria a cor dizendo
 * sozinha. Só o último ponto, que é o número do mês, ganha o ponto marcado. Cada dia tem o
 * `title` com o valor — o passar do mouse —, e a descrição inteira vai no `aria-label`.
 */
export function TendenciaDoIndice({
  pontos,
  minimo,
  descricao,
}: {
  pontos: PontoDaTendencia[];
  minimo: number | null;
  descricao: string;
}) {
  const [caixa, definirCaixa] = useState<HTMLDivElement | null>(null);
  const [largura, definirLargura] = useState(220);
  useEffect(() => {
    if (!caixa) return;
    const medir = () => definirLargura(Math.max(120, caixa.clientWidth));
    medir();
    if (typeof ResizeObserver === "undefined") return;
    const observador = new ResizeObserver(medir);
    observador.observe(caixa);
    return () => observador.disconnect();
  }, [caixa]);

  const valores = pontos.map((p) =>
    p.percentual == null ? null : Number(p.percentual),
  );
  const contados = valores.filter((v): v is number => v != null);
  if (pontos.length === 0 || contados.length === 0) {
    return (
      <p className="text-[10.5px] text-tinta-3" role="status">
        Sem tendência: nada contado no mês ainda.
      </p>
    );
  }

  const A = 44; // altura da área
  const M = 4; // margem, para o ponto e a linha de 2 px não cortarem
  // O piso: um ponto abaixo do menor valor ou da meta — sem ele, 94% e 99% ficam colados.
  const piso = Math.max(
    0,
    Math.floor(Math.min(...contados, minimo ?? 100)) - 1,
  );
  const teto = 100;
  const faixa = pontos.length > 1 ? (largura - 2 * M) / (pontos.length - 1) : 0;
  const x = (i: number) => M + i * faixa;
  const y = (v: number) =>
    M + ((teto - v) / Math.max(1, teto - piso)) * (A - 2 * M);

  const caminho = valores
    .map((v, i) => (v == null ? null : `${x(i)} ${y(v)}`))
    .reduce<string[]>((partes, ponto, i) => {
      if (ponto == null) return partes;
      const antes = i > 0 && valores[i - 1] != null;
      partes.push(`${antes ? "L" : "M"} ${ponto}`);
      return partes;
    }, [])
    .join(" ");
  const ultimo = valores.length - 1;
  const ultimoValor = valores[ultimo];

  return (
    <div ref={definirCaixa} className="min-w-0">
      <svg
        viewBox={`0 0 ${largura} ${A}`}
        width={largura}
        height={A}
        className="block max-w-full"
        role="img"
        aria-label={descricao}
      >
        {minimo != null && minimo >= piso && (
          <line
            x1={M}
            x2={largura - M}
            y1={y(minimo)}
            y2={y(minimo)}
            className="stroke-tinta-3"
            strokeWidth={1}
            strokeDasharray="3 3"
          />
        )}
        <path
          d={caminho}
          fill="none"
          className="stroke-azul"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {ultimoValor != null && (
          <circle
            cx={x(ultimo)}
            cy={y(ultimoValor)}
            r={3}
            className="fill-azul stroke-painel-2"
            strokeWidth={2}
          />
        )}
        {/* O alvo de cada dia é a faixa inteira dele, maior que a linha. */}
        {pontos.map((p, i) => (
          <rect
            key={p.dia}
            x={Math.max(0, x(i) - Math.max(faixa, 6) / 2)}
            y={0}
            width={Math.max(faixa, 6)}
            height={A}
            fill="transparent"
          >
            <title>
              {diaDoCalendario(p.dia)}:{" "}
              {p.percentual == null
                ? "nada contado ainda"
                : `${p.percentual.replace(".", ",")}% no acumulado do mês`}
            </title>
          </rect>
        ))}
      </svg>
    </div>
  );
}

function Legenda({ cor, children }: { cor: string; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`size-2.5 rounded-[2px] ${cor}`} />
      {children}
    </span>
  );
}

function Vazio({ children }: { children: ReactNode }) {
  return (
    <p
      className="px-2 py-6 text-center text-[11.5px] text-tinta-3"
      role="status"
    >
      {children}
    </p>
  );
}
